import {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  Divider,
  Link,
  Spinner,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {NotImplemented} from '@amblique/contentful/app/components/contentful/not-implemented';
import {Markup} from '@amblique/base/app/components/shared/markup';

/**
 * Timeout duration for iframe loading (in milliseconds).
 * If iframe doesn't load within this time, an error state is shown.
 */
const IFRAME_LOAD_TIMEOUT_MS = 5000;

/**
 * @override
 * Custom implementation for EmbedPanel component
 * - Renders iframe or HTML content based on Contentful data
 * - Shows NotImplemented component while loading or incomplete
 * - Applies dynamic width/height from Contentful fields
 * - Handles iframe loading and error states
 * - Applies sandbox security attributes to iframe
 *
 * @param {Object} props - Component props
 * @param {Contentful.EmbedPanel} props.component - EmbedPanel component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const EmbedPanel = ({component, isComplete = true, isFetching = false, ...props}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('contentful/embed-panel', {component, isComplete, isFetching});
  const scriptContainerRef = useRef(null);
  const timeoutRef = useRef(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  const {iframeUrl, htmlContent, scriptContent, height, separator} = component || {};

  // Handle iframe loading timeout
  // If iframe doesn't load within IFRAME_LOAD_TIMEOUT_MS, show error state
  useEffect(() => {
    if (iframeUrl && iframeLoading) {
      timeoutRef.current = setTimeout(() => {
        setIframeLoading(false);
        setIframeError(true);
      }, IFRAME_LOAD_TIMEOUT_MS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [iframeUrl, iframeLoading]);

  // Show NotImplemented component while loading or incomplete
  if (isFetching) {
    return <NotImplemented component={component} isComplete={isComplete} isFetching={isFetching} {...props} />;
  }

  // Don't render if no content
  if (!iframeUrl && !htmlContent) {
    return null;
  }

  // Handle iframe load events
  const handleIframeLoad = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIframeLoading(false);
    setIframeError(false);
  };

  const handleIframeError = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIframeLoading(false);
    setIframeError(true);
  };

  // Inject script content as raw HTML that will execute
  useEffect(() => {
    if (scriptContent && scriptContainerRef.current) {
      // Use innerHTML to inject raw script content
      scriptContainerRef.current.innerHTML = scriptContent;

      // Extract and execute script tags (scripts in innerHTML don't auto-execute)
      const scripts = scriptContainerRef.current.querySelectorAll('script');

      scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');

        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode?.replaceChild(newScript, oldScript);
      });
    }
  }, [scriptContent]);

  // Render error state for iframe
  if (iframeError && iframeUrl) {
    return (
      <Box
        {...styles.errorContainer}
        {...props}
      >
        <Box
          {...styles.error}
          p={6}
          textAlign="center"
        >
          <Text
            color="gray.700"
            fontSize="md"
            mb={3}
          >
            {intl.formatMessage({
              defaultMessage: 'Unable to load form. Please contact us directly.',
              id: 'embed_panel.error.unable_to_load',
            })}
          </Text>
          <Link
            color="blue.600"
            fontSize="lg"
            fontWeight="semibold"
            href="tel:1300-546-454"
          >
            {intl.formatMessage({
              defaultMessage: '1300 KING LIVING',
              id: 'embed_panel.contact.phone_number',
            })}
          </Link>
        </Box>
      </Box>
    );
  }

  return (
    <Box {...styles.container} {...props}>
      {separator && (
        <Divider {...styles.separator} />
      )}

      {iframeUrl ? (
        <Box
          {...styles.iframeWrapper}
          position="relative"
        >
          {iframeLoading && (
            <Box
              {...styles.loading}
              alignItems="center"
              display="flex"
              height="100%"
              justifyContent="center"
              left={0}
              position="absolute"
              top={0}
              width="100%"
            >
              <Spinner
                color="blue.500"
                size="lg"
              />
              <Text
                color="gray.600"
                ml={3}
              >
                {intl.formatMessage({
                  defaultMessage: 'Loading form...',
                  id: 'embed_panel.loading.loading_form',
                })}
              </Text>
            </Box>
          )}
          <Box
            {...styles.iframe}
            as="iframe"
            height={height || '800px'}
            loading="lazy"
            opacity={iframeLoading ? 0 : 1}
            sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            src={iframeUrl}
            transition="opacity 0.3s ease-in-out"
            width="100%"
            title={intl.formatMessage({
              defaultMessage: 'Embedded Form',
              id: 'embed_panel.iframe.title',
            })}
            onError={handleIframeError}
            onLoad={handleIframeLoad}
          />
        </Box>
      ) : htmlContent ? (
        <Box {...styles.htmlContent}>
          <Markup source={htmlContent} />
        </Box>
      ) : null}

      {scriptContent && (
        <Box
          ref={scriptContainerRef}
          display="none"
        />
      )}
    </Box>
  );
};

EmbedPanel.displayName = 'EmbedPanel';
EmbedPanel.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
